import { createSelector } from '@reduxjs/toolkit';
import type { LexicalEditor, SerializedEditorState } from 'lexical';

import type { LexicalEditorsDataType, TranscriptData, TranscriptTranslations } from '.';

import { LanguageEnum } from 'js/src/client';
import StoreORM from 'js/src/data/store-orm';
import {
    $getSilenceSpans,
    $getUntrimmedFillerWords,
} from 'js/src/data/transcript/transcript-utils';
import type { TranslatedEditorsType } from 'js/src/data/translated-editors';
import { getTranslatedEditors } from 'js/src/data/translated-editors';
import { type ImmutableMap, UNKNOWN_LANGUAGE_CODE } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function isTranscriptStateAvailable(state: RootState) {
    return !!state.transcript;
}

export function isTranscriptVisible(state: RootState) {
    return state.transcript.transcript_visible;
}

export function isSceneConversionModalVisible(state: RootState) {
    return state.transcript.scene_conversion_modal_visible;
}

export function getTranscriptState(state: RootState) {
    return state.undoable.present.transcript;
}

export function getTranscripts(state: RootState) {
    return getTranscriptState(state).transcripts;
}

export function getTranscriptById(state: RootState, sceneGroupId: string) {
    const orm = new StoreORM<TranscriptData, string>(getTranscripts(state));
    return orm.getByIdOrUndefined(sceneGroupId);
}

export function getTranscriptTranslationMap(state: RootState, sceneGroupId: string) {
    const orm = new StoreORM<TranscriptData, string>(getTranscripts(state));
    const transcript = orm.getByIdOrUndefined(sceneGroupId);
    const translations = transcript?.get('translations');
    return translations;
}

/**
 * Get the translation language for a transcript specified by the scene group id.
 * For now, we only support translating the transcript into a single other language.
 * But we store the data as an Immutable.Map so that we could expand that in the future if we wanted to.
 * @param state redux state
 * @param sceneGroupId the id of the scene group for which we want the translation data
 * @returns either the language code (eg. "en", "fe", "de", etc) or undefined if the transcript has not been translated
 */
export function getTranscriptTranslationLanguage(
    state: RootState,
    sceneGroupId: string,
): LanguageEnum | undefined {
    const translations = getTranscriptTranslationMap(state, sceneGroupId);
    return translations?.keySeq().get(0); // could be undefined, if there are no translations
}

/**
 * Get the editorState for the language that was translated
 * @param state redux state
 * @param sceneGroupId the id of the scene group for which we want the translation data
 * @returns the editorState of the translated transcript
 */
export function getTranscriptTranslationById(state: RootState, sceneGroupId: string) {
    const translations = getTranscriptTranslationMap(state, sceneGroupId);
    const languageCode = getTranscriptTranslationLanguage(state, sceneGroupId);
    if (!translations || !languageCode) {
        return undefined;
    }
    return translations.get(languageCode).get('editorState');
}

export function sceneGroupHasTranscriptTranslation(state: RootState, sceneGroupId: string) {
    return !!getTranscriptTranslationLanguage(state, sceneGroupId);
}

export function shouldDisableOverlays(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (transcript) {
        return transcript.get('disableOverlays');
    }
    return false;
}

export function getIsOriginalTranscript(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (transcript) {
        return transcript.get('isOriginal');
    }
    return false;
}

export function getIsOriginalOrMissingTranscript(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (!transcript) {
        return true;
    }
    return transcript.get('isOriginal');
}

export function getOriginalTranscriptLanguage(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    const language = transcript?.get('language');
    if (!language) {
        return UNKNOWN_LANGUAGE_CODE;
    }
    return language;
}

export function getTranscriptIds(state: RootState) {
    const orm = new StoreORM<TranscriptData, string>(getTranscripts(state));
    return orm.getAllIds();
}

export const getAllTranscriptEditorStates = createSelector(
    (state: RootState) => getTranscripts(state),
    (transcripts) => {
        const orm = new StoreORM<TranscriptData, string>(transcripts);
        const rawTranscripts: Record<string, ImmutableMap<SerializedEditorState>> = {};
        orm.getAllIds().forEach((id) => {
            const editorState = orm.getById(id).get('editorState');
            rawTranscripts[String(id)] = editorState;
        });
        return rawTranscripts;
    },
);

export const getTranscriptEditorStateById = createSelector(
    (state: RootState, sceneGroupId: string) => getAllTranscriptEditorStates(state),
    (state: RootState, sceneGroupId: string) => sceneGroupId,
    (transcripts, sceneGroupId) => {
        return transcripts[sceneGroupId];
    },
);

export function getTranscriptsToSave(state: RootState) {
    const orm = new StoreORM<TranscriptData, string>(getTranscripts(state));
    const transcripts: Record<
        string,
        {
            disableOverlays: boolean;
            editorState: ImmutableMap<SerializedEditorState>;
            translations: ImmutableMap<TranscriptTranslations> | undefined;
            language: string;
        }
    > = {};
    orm.getAllIds().forEach((id) => {
        const transcript = orm.getById(id);
        const editorState = transcript.get('editorState');
        const disableOverlays = !!transcript.get('disableOverlays');
        const translations = transcript.get('translations');
        const language = transcript.get('language') ?? LanguageEnum.XX;
        transcripts[String(id)] = {
            disableOverlays,
            editorState,
            translations,
            language,
        };
    });
    return transcripts;
}

export function getTranscriptCurrentTime(state: RootState) {
    return state.transcript.current_time;
}

export function getTranscriptCurrentTimeRelativeToSceneGroup(state: RootState) {
    const currentTime = getTranscriptCurrentTime(state);
    return currentTime.get('currentTimeMillis');
}

export const getLexicalEditors = (state: RootState) => {
    return state.transcript.lexical_editors;
};

export const getEditorFromSceneGroupId = (state: RootState, sceneGroupId: string) => {
    const orm = new StoreORM<LexicalEditorsDataType, string>(getLexicalEditors(state));

    const lexicalEditorsData = orm.getByIdOrUndefined(sceneGroupId);
    if (!lexicalEditorsData) {
        return undefined;
    }

    // we need to do casting because the LexicalEditor won't actually be an Immutable
    // inside of the Immutable wrapper, but typescript doesn't know that.
    return lexicalEditorsData.get('editor') as unknown as LexicalEditor;
};

export const getLexicalControlsItsOwnState = (
    state: RootState,
    sceneGroupId: string,
    langCode?: LanguageEnum | null,
) => {
    if (!langCode) {
        // we are dealing with the main transcript. Look up the lexicalControlsItsOwnState
        // value in that part of the redux store
        const orm = new StoreORM<LexicalEditorsDataType, string>(getLexicalEditors(state));
        const lexicalControlsItsOwnState = orm
            .getByIdOrUndefined(sceneGroupId)
            ?.get('lexicalControlsItsOwnState');
        return !!lexicalControlsItsOwnState;
    } else {
        // we are dealing with a translated transcript. Look up the lexicalControlsItsOwnState
        // value in that part of the redux store
        const orm = new StoreORM<TranslatedEditorsType, string>(getTranslatedEditors(state));
        const translatedEditorsData = orm.getById(sceneGroupId);
        const lexicalControlsItsOwnState = translatedEditorsData
            ?.get('translations')
            ?.get(langCode)
            ?.get('lexicalControlsItsOwnState');
        return !!lexicalControlsItsOwnState;
    }
};

export function getTimingMode(state: RootState) {
    return state.transcript.timing_mode;
}

export function isTimingModeActive(state: RootState) {
    const timingMode = getTimingMode(state);
    return timingMode.get('assetId') !== null;
}

export function getTimingModeAssetId(state: RootState) {
    const timingMode = getTimingMode(state);
    return timingMode.get('assetId');
}

export function getTimingModeSceneId(state: RootState) {
    const timingMode = getTimingMode(state);
    return timingMode.get('sceneId');
}

export function getTimingModeSceneGroupId(state: RootState) {
    const timingMode = getTimingMode(state);
    return timingMode.get('sceneGroupId');
}

export function hasRemovableFillerWords(state: RootState, sceneGroupId: string): boolean {
    const editor = getEditorFromSceneGroupId(state, sceneGroupId);
    if (!editor) {
        return false;
    }
    let result = false;
    editor.getEditorState().read(() => {
        result = $getUntrimmedFillerWords().length > 0;
    });
    return result;
}

export function hasRemovableSilences(
    state: RootState,
    sceneGroupId: string,
    minDuration: number,
): boolean {
    const editor = getEditorFromSceneGroupId(state, sceneGroupId);
    if (!editor) {
        return false;
    }
    let result = false;
    editor.getEditorState().read(() => {
        result = $getSilenceSpans().some(
            ([start, end]) => end.endTime - start.startTime >= minDuration,
        );
    });
    return result;
}
